import { useRef } from "react";
import axios from "axios";
import { Camera, ImagePlus, Loader2, X, AlertCircle } from "lucide-react";

const UPLOAD = `${process.env.REACT_APP_BACKEND_URL}/api/app/public/quote-photos`;
export const MAX_PHOTOS = 8;
const MAX_MB = 10;
const OK_TYPE = /^image\/(jpe?g|png|heic|heif)$/i;
const OK_EXT = /\.(jpe?g|png|heic|heif)$/i;

// Resizes to ~2000 px JPEG in the browser; HEIC that the browser cannot decode is sent as-is (converted server-side).
async function compress(file) {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const k = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    const c = Object.assign(document.createElement("canvas"), { width: Math.round(bmp.width * k), height: Math.round(bmp.height * k) });
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.85));
    return blob ? { blob, name: file.name.replace(/\.[^.]+$/, "") + ".jpg", preview: URL.createObjectURL(blob) } : { blob: file, name: file.name, preview: null };
  } catch {
    return { blob: file, name: file.name, preview: null };
  }
}

export const PhotoPicker = ({ photos, setPhotos, website }) => {
  const pick = useRef(null);
  const cam = useRef(null);
  const patch = (key, ch) => setPhotos((list) => list.map((p) => (p.key === key ? { ...p, ...ch } : p)));

  const add = async (files) => {
    const room = MAX_PHOTOS - photos.length;
    for (const file of Array.from(files).slice(0, Math.max(room, 0))) {
      const key = `${Date.now()}-${Math.random()}`;
      const bad = !(OK_TYPE.test(file.type) || OK_EXT.test(file.name)) ? "Format non supporté (JPG, PNG, HEIC)" : file.size > MAX_MB * 1024 * 1024 ? `Trop lourde (${MAX_MB} Mo max.)` : null;
      setPhotos((list) => [...list, { key, name: file.name, status: bad ? "error" : "uploading", error: bad }]);
      if (bad) continue;
      const c = await compress(file);
      patch(key, { preview: c.preview });
      const fd = new FormData();
      fd.append("file", c.blob, c.name);
      fd.append("website", website || "");
      try {
        const { data } = await axios.post(UPLOAD, fd);
        patch(key, { id: data.id, status: "done" });
      } catch (e) {
        patch(key, { status: "error", error: e.response?.data?.detail || "Envoi impossible" });
      }
    }
  };
  const onFiles = (e) => { add(e.target.files); e.target.value = ""; };
  const full = photos.length >= MAX_PHOTOS;

  return (
    <div className="sm:col-span-2" data-testid="quote-photos">
      <div className="mb-1.5 text-sm font-semibold text-cs-navy">Photos du chantier <span className="font-normal text-cs-ink/60">(facultatif, {MAX_PHOTOS} max., JPG/PNG/HEIC, {MAX_MB} Mo max.)</span></div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={full} onClick={() => pick.current.click()} className="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-cs-navy/25 px-4 py-3 text-sm font-semibold text-cs-navy transition-colors hover:border-cs-yellow disabled:opacity-50" data-testid="quote-photos-add">
          <ImagePlus className="h-5 w-5" /> Ajouter des photos
        </button>
        <button type="button" disabled={full} onClick={() => cam.current.click()} className="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-cs-navy/25 px-4 py-3 text-sm font-semibold text-cs-navy transition-colors hover:border-cs-yellow disabled:opacity-50 md:hidden" data-testid="quote-photos-camera">
          <Camera className="h-5 w-5" /> Prendre une photo
        </button>
      </div>
      <input ref={pick} type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" className="hidden" onChange={onFiles} data-testid="quote-photos-input" />
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} data-testid="quote-photos-camera-input" />
      {photos.length > 0 && (
        <ul className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-8" data-testid="quote-photos-list">
          {photos.map((p, i) => (
            <li key={p.key} className="relative aspect-square overflow-hidden rounded-lg bg-slate-100" title={p.error || p.name} data-testid={`quote-photo-${i}`}>
              {p.preview ? <img src={p.preview} alt={p.name} className="h-full w-full object-cover" /> : <span className="grid h-full w-full place-items-center p-1 text-center text-[10px] text-cs-ink/60">{p.name}</span>}
              {p.status === "uploading" && <span className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="h-5 w-5 animate-spin text-cs-navy" /></span>}
              {p.status === "error" && <span className="absolute inset-0 grid place-items-center bg-red-600/80 p-1 text-center text-[10px] font-semibold text-white" data-testid={`quote-photo-error-${i}`}><AlertCircle className="h-4 w-4" />{p.error}</span>}
              <button type="button" onClick={() => setPhotos((list) => list.filter((x) => x.key !== p.key))} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-cs-navy text-white" aria-label={`Retirer ${p.name}`} data-testid={`quote-photo-remove-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
